import { useQuery } from '@tanstack/react-query';

import { ICON_URL_STALE_TIME } from '../../app/constants';
import { queryClient } from '../../api/queryClient';
import {
  getV1BusinessPartnerCurrentDivisionsOptions,
  getV1BusinessPartnerCurrentDivisionsQueryKey,
} from '../../api/generated/pap/@tanstack/react-query.gen';
import {
  getV1RequestBusinessPartnerDivisionsOptions,
  getV1RequestBusinessPartnerDivisionsQueryKey,
} from '../../api/generated/pm/@tanstack/react-query.gen';

/**
 * Query hooks for the current Business Partner's Divisions: the Divisions
 * themselves (PAP) and the pending requests for new Divisions (PM), which
 * `src/hooks/useAggregateDivisions.tsx` merges into one list. See
 * `../Settings/IdentityProviders/identityProviders.queries.ts` for the pattern.
 */

/** Icon URLs in the response are presigned and expire, hence the stale time. */
export const useCurrentBusinessPartnerDivisions = () =>
  useQuery({
    ...getV1BusinessPartnerCurrentDivisionsOptions(),
    staleTime: ICON_URL_STALE_TIME,
    refetchOnMount: true,
  });

export const invalidateCurrentBusinessPartnerDivisions = () =>
  queryClient.invalidateQueries({ queryKey: getV1BusinessPartnerCurrentDivisionsQueryKey() });

/**
 * Same freshness as the Divisions list it is merged with. `refetchOnMount` is
 * off app-wide, so without it a clear made while no page shows the requests
 * (e.g. a Settings → Access save) would never reach the next Divisions or
 * Overview visit.
 */
export const useCurrentBusinessPartnerDivisionRequests = (enabled: boolean = true) =>
  useQuery({
    ...getV1RequestBusinessPartnerDivisionsOptions(),
    enabled,
    staleTime: ICON_URL_STALE_TIME,
    refetchOnMount: true,
  });

export const invalidateCurrentBusinessPartnerDivisionRequests = () =>
  queryClient.invalidateQueries({ queryKey: getV1RequestBusinessPartnerDivisionsQueryKey() });
